const UNSAFE_SERVER_MESSAGE = new RegExp([
  'sql',
  'stack\\s*trace',
  '\\bat\\s+[\\w.$]+\\(',
  'syntax error',
  'org\\.springframework',
  'psqlexception',
  'caused by:',
].join('|'), 'i');

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isJsonObject(value) {
  return Boolean(value)
    && typeof value === 'object'
    && !Array.isArray(value)
    && !(typeof Blob !== 'undefined' && value instanceof Blob);
}

/**
 * @param {unknown} headers
 * @param {string} name
 * @returns {string}
 */
function readResponseHeader(headers, name) {
  if (!headers || typeof headers !== 'object') {
    return '';
  }

  const lower = name.toLowerCase();
  if (typeof headers.get === 'function') {
    const fromGetter = headers.get(name) ?? headers.get(lower);
    return typeof fromGetter === 'string' ? fromGetter.trim() : '';
  }

  const record = /** @type {Record<string, unknown>} */ (headers);
  const value = record[lower] ?? record[name];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * @param {unknown} message
 * @returns {boolean}
 */
function isUnsafeServerMessage(message) {
  return typeof message === 'string' && UNSAFE_SERVER_MESSAGE.test(message);
}

/**
 * 傳輸層錯誤（無 HTTP response）→ 中文文案。
 * 有 `error.response` 時回 `null`，交給各模組 `helper/apiError` 處理業務碼。
 *
 * @param {unknown} error
 * @returns {string|null}
 */
export function getRequestTransportErrorMessage(error) {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const err = /** @type {{ response?: unknown, request?: unknown, code?: string, message?: string }} */ (
    error
  );

  if (err.response) {
    return null;
  }

  const code = err.code ?? '';
  const message = String(err.message ?? '');

  if (
    code === 'ECONNABORTED'
    || code === 'ETIMEDOUT'
    || /timeout/i.test(message)
  ) {
    return '連線逾時，請稍後再試';
  }

  if (
    code === 'ERR_NETWORK'
    || message === 'Network Error'
    || /network error/i.test(message)
    || Boolean(err.request)
  ) {
    return '網路連線異常，請檢查網路後再試';
  }

  return null;
}

/**
 * 非 2xx JSON 的錯誤 envelope。沒有 HTTP response 時回 `null`。
 * `field`／`details` 缺漏時分別為 `null`／`undefined`。request ID 先用 body `traceId`（報價單 route 為 `trace_id`），沒有再用 `X-Request-Id`。
 *
 * @param {unknown} error
 * @returns {{
 *   status: number,
 *   code: string,
 *   message: string,
 *   field: string|null,
 *   details: unknown,
 *   requestId: string,
 * }|null}
 */
export function readApiErrorEnvelope(error) {
  if (!error || typeof error !== 'object' || !('response' in error) || !error.response) {
    return null;
  }

  const response = /** @type {{ status?: number, data?: unknown, headers?: unknown }} */ (
    error.response
  );
  const data = isJsonObject(response.data) ? response.data : null;
  const rawTraceId = data?.traceId ?? data?.trace_id;
  const bodyTraceId = typeof rawTraceId === 'string' ? rawTraceId.trim() : '';
  const headerRequestId = readResponseHeader(response.headers, 'X-Request-Id');

  return {
    status: Number(response.status) || 0,
    code: typeof data?.code === 'string' ? data.code : '',
    message: typeof data?.message === 'string' ? data.message.trim() : '',
    field: typeof data?.field === 'string' ? data.field : null,
    details: data && Object.prototype.hasOwnProperty.call(data, 'details') ? data.details : undefined,
    requestId: bodyTraceId || headerRequestId,
  };
}

/**
 * @param {string} message
 * @param {string} [requestId]
 * @returns {string}
 */
export function formatErrorWithRequestId(message, requestId) {
  const text = String(message || '').trim();
  const id = typeof requestId === 'string' ? requestId.trim() : '';
  if (!id || text.includes(id)) {
    return text;
  }
  return `${text}（要求編號 ${id}）`;
}

/**
 * HTTP 500（或 `INTERNAL_SERVER_ERROR`）的安全文案。不是伺服器錯誤時回 `null`。
 *
 * @param {unknown} error
 * @param {string} fallback
 * @returns {string|null}
 */
export function getSafeServerErrorMessage(error, fallback) {
  const envelope = readApiErrorEnvelope(error);
  if (!envelope) {
    return null;
  }

  const isServerError = envelope.status >= 500 || envelope.code === 'INTERNAL_SERVER_ERROR';
  if (!isServerError) {
    return null;
  }

  const message = envelope.message && !isUnsafeServerMessage(envelope.message)
    ? envelope.message
    : fallback;
  return formatErrorWithRequestId(message, envelope.requestId);
}

/**
 * 通用讀取／操作錯誤文案：傳輸層優先，其次安全的伺服器文案，再來 response.message。
 * 非 2xx（含 409）維持失敗，不把錯誤 body 當成成功。
 *
 * @param {unknown} error
 * @param {string} [fallback]
 * @returns {string}
 */
export function getRequestErrorMessage(error, fallback = '操作失敗，請稍後再試') {
  const transportMessage = getRequestTransportErrorMessage(error);
  if (transportMessage) {
    return transportMessage;
  }

  const serverMessage = getSafeServerErrorMessage(error, fallback);
  if (serverMessage) {
    return serverMessage;
  }

  const envelope = readApiErrorEnvelope(error);
  if (envelope?.message && !isUnsafeServerMessage(envelope.message)) {
    return envelope.message;
  }

  return fallback;
}
